import { toastServerError } from "helpers";
import moment from "moment";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Loader, Modal, Switch } from "ui";

import { useMarketingContact, useUpdateContactConsent } from "../../apis";
import {
  CHANNEL_COLORS,
  CHANNEL_LABELS,
  CONSENT_STATUS_COLORS,
  CONSENT_STATUS_LABELS,
  CONTACT_EVENT_TYPE_LABELS,
  CONTACT_SOURCE_LABELS
} from "../../constants";
import { EMarketingConsentStatus } from "../../marketing.types";
import { StatusBadge } from "../StatusBadge";

import styles from "./ContactDetailsModal.module.scss";

interface ContactDetailsModalProps {
  phone: string | null;
  onClose: () => void;
}

const ContactDetailsModal = ({ phone, onClose }: ContactDetailsModalProps) => {
  const { t } = useTranslation();

  const { contact, events, isLoading } = useMarketingContact(phone ?? "");

  const { mutate: updateConsent, isLoading: isConsentUpdating } =
    useUpdateContactConsent({
      onSuccess: () => toast.success(t("Consent updated")),
      onError: toastServerError
    });

  const infoRows = contact
    ? [
        { label: t("Phone"), value: `+${contact.phoneNumber}` },
        { label: t("Name"), value: contact.name ?? "—" },
        {
          label: t("Source"),
          value: contact.source ? t(CONTACT_SOURCE_LABELS[contact.source]) : "—"
        },
        {
          label: t("App installed"),
          value: contact.appInstalled ? t("Yes") : t("No")
        },
        {
          label: t("Last order"),
          value: contact.lastOrderAt
            ? moment(contact.lastOrderAt).format("DD/MM/YYYY HH:mm")
            : "—"
        },
        {
          label: t("Last app activity"),
          value: contact.lastAppActivityAt
            ? moment(contact.lastAppActivityAt).format("DD/MM/YYYY HH:mm")
            : "—"
        },
        {
          label: t("Added"),
          value: moment(contact.createdAt).format("DD/MM/YYYY HH:mm")
        }
      ]
    : [];

  return (
    <Modal
      isOpen={Boolean(phone)}
      title={contact?.name ?? (phone ? `+${phone}` : "")}
      footer={null}
      onClose={onClose}
    >
      {isLoading || !contact ? (
        <Loader />
      ) : (
        <div className={styles.content}>
          <div className={styles.info}>
            {infoRows.map((row) => (
              <div key={row.label} className={styles.info__row}>
                <span className={styles.info__label}>{row.label}</span>
                <span className={styles.info__value}>{row.value}</span>
              </div>
            ))}
          </div>

          <div className={styles.consent}>
            <div className={styles.consent__status}>
              <span className={styles.info__label}>
                {t("WhatsApp marketing consent")}
              </span>
              <StatusBadge
                label={CONSENT_STATUS_LABELS[contact.whatsappConsentStatus]}
                color={CONSENT_STATUS_COLORS[contact.whatsappConsentStatus]}
              />
            </div>

            <Switch
              label={t("Opted in")}
              checked={
                contact.whatsappConsentStatus ===
                EMarketingConsentStatus.OptedIn
              }
              disabled={isConsentUpdating}
              onChange={(event) =>
                updateConsent({
                  phone: contact.phoneNumber,
                  optedIn: event.target.checked
                })
              }
            />

            {contact.whatsappConsentUpdatedAt && (
              <span className={styles.consent__updated}>
                {t("Updated")}{" "}
                {moment(contact.whatsappConsentUpdatedAt).format(
                  "DD/MM/YYYY HH:mm"
                )}
              </span>
            )}
          </div>

          <div className={styles.events}>
            <span className={styles.events__title}>{t("Recent events")}</span>

            {events.length === 0 && (
              <span className={styles.events__empty}>{t("No events yet")}</span>
            )}

            {events.map((event) => (
              <div key={event.id} className={styles.events__row}>
                <span className={styles.events__type}>
                  {t(CONTACT_EVENT_TYPE_LABELS[event.type] ?? event.type)}
                </span>

                {event.channel && (
                  <StatusBadge
                    label={CHANNEL_LABELS[event.channel]}
                    color={CHANNEL_COLORS[event.channel]}
                  />
                )}

                <span className={styles.events__date}>
                  {moment(event.createdAt).format("DD/MM/YYYY HH:mm")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ContactDetailsModal;
